import { useEffect, useRef, useState } from "react";
import {
  LineChart,
  useXScale,
  useDrawingArea,
  MarkElement,
} from "@mui/x-charts";

import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import dayjs from "dayjs";
import {
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../../../utils/configurationProvider";

/**
 * CommonLineGraph - A reusable dual-line chart component for statistics visualization.
 * Renders one or two area line series with month grouping labels on the x-axis.
 * Supports dynamic y-axis scaling, amount formatting, and CSS variable theming.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number[]} props.graphDataFirst - Primary data series (required)
 * @param {number[]} [props.graphDataSecond] - Secondary data series (optional)
 * @param {string[]} props.xAxisLabels - Date/day labels for x-axis (e.g., ["2025-01-01", ...] or ["Sun", "Mon"])
 * @param {string} props.graphDataFirstColor - CSS variable for first series color (e.g., "var(--settled)")
 * @param {string} [props.graphDataSecondColor] - CSS variable for second series color
 * @param {boolean} [props.enableDynamicZeroColoring] - Whether to apply dynamic coloring to second series based on zero/non-zero values
 * @param {string} [props.zeroValueColor] - CSS variable for zero values in second series
 * @param {string} [props.nonZeroValueColor] - CSS variable for non-zero values in second series
 * @param {number[]} [props.defaultDataItemsForXaxis] - Custom x-axis indices (falls back to 0..n)
 * @param {string} [props.period] - Time period string
 * @param {boolean} [props.amountFormatRequiredForLegend=true] - Whether to format tooltip values as INR amounts
 * @param {string} [props.amountFormatForLegend] - Amount unit for legend/tooltip (e.g., "thousand", "lakh", "crore")
 * @param {boolean} [props.normalizePositiveAndNegativeValues=false] - Scale positive and negative values independently around zero
 * @returns {JSX.Element} Rendered LineChart with custom styling and month labels
 */
const CommonLineGraph = ({
  graphDataFirst,
  graphDataSecond,
  xAxisLabels, // e.g. ["2025-01-01", "2025-01-02", ..., "2025-02-01"] or ["Sun", "Mon"]
  graphDataFirstColor,
  graphDataSecondColor = "var(--text-primary)",
  enableDynamicZeroColoring = false,
  zeroValueColor = "var(--failed)",
  nonZeroValueColor = "var(--settled)",
  defaultDataItemsForXaxis,
  period,
  amountFormatRequiredForLegend = true,
  amountFormatForLegend,
  normalizePositiveAndNegativeValues = false,
}) => {
  const [highlightedItemIndex, setHighlightedItemIndex] = useState(null);
  const [highlightedAxis, setHighlightedAxis] = useState([]);
  const [tooltipAxis, setTooltipAxis] = useState([]);

  /**
   * True only while the pointer is directly over a synthetic
   * zero-crossing point.
   *
   * Synthetic points are visual-only points used to split the
   * line exactly at zero. They must never show an axis highlight
   * or tooltip.
   */
  const [isSyntheticCrossingHovered, setIsSyntheticCrossingHovered] =
    useState(false);
  let xAxisArray = xAxisLabels;

  //NOTE: this is fallback function to check wheather data (xAxisLabels) coming from backend is day or date format
  const isDateArray = (data) =>
    data &&
    data.length > GlobalConfiguration?.lengthCheckZero &&
    data.every((item) => /^\d{4}-\d{2}-\d{2}$/.test(item));

  // Abbreviation map for amount units in y-axis labels
  const abbrMap = {
    Lakhs: "L",
    Crores: "Cr",
    Millions: "M",
    Billions: "B",
    Thousands: "K",
  };

  const formattedGraphDataFirst = graphDataFirst
    ? graphDataFirst.map((value) => value)
    : [];
  const formattedGraphDataSecond = graphDataSecond
    ? graphDataSecond.map((value) => value)
    : [];

  // Whether a data point is currently hovered (highlighted)
  const isHovered =
    highlightedItemIndex !== null && highlightedItemIndex !== undefined;

  // Value of second series at hovered index (or 0 if no second data)
  const hoveredSecondVal = isHovered
    ? (formattedGraphDataSecond[highlightedItemIndex] ??
      GlobalConfiguration?.lengthCheckZero)
    : null;

  const isHoveredSecondZero =
    hoveredSecondVal === GlobalConfiguration?.lengthCheckZero;

  // Whether second series has any non-zero values (used for gradient coloring)
  const hasSecondDataNonZero =
    enableDynamicZeroColoring &&
    formattedGraphDataSecond.some(
      (val) => val > GlobalConfiguration?.lengthCheckZero,
    );

  // --- Dynamic min/max ---
  const allData = [...formattedGraphDataFirst, ...formattedGraphDataSecond];
  const finiteValues = allData.map(Number).filter(Number.isFinite);
  const largestPositiveValue = Math.max(0, ...finiteValues);
  const largestNegativeMagnitude = Math.abs(Math.min(0, ...finiteValues));
  const negativeScaleStep =
    largestNegativeMagnitude > 0
      ? 10 ** (Math.floor(Math.log10(largestNegativeMagnitude)) - 1)
      : 0;
  const negativeScaleMagnitude = negativeScaleStep
    ? Math.ceil(largestNegativeMagnitude / negativeScaleStep) *
      negativeScaleStep
    : 0;
  const splitBySign =
    normalizePositiveAndNegativeValues && largestNegativeMagnitude > 0;
  const useIndependentSignScale = splitBySign && largestPositiveValue > 0;

  const toChartValue = (value) => {
    const numericValue = Number(value);
    if (!useIndependentSignScale || !Number.isFinite(numericValue)) {
      return numericValue;
    }
    return numericValue >= 0
      ? numericValue / largestPositiveValue
      : numericValue / negativeScaleMagnitude;
  };
  const fromChartValue = (value) => {
    if (!useIndependentSignScale) return Number(value);
    return value >= 0
      ? value * largestPositiveValue
      : value * negativeScaleMagnitude;
  };

  const chartDataFirst = formattedGraphDataFirst.map(toChartValue);
  const chartDataSecond = formattedGraphDataSecond.map(toChartValue);
  const yAxisMin = useIndependentSignScale
    ? -1
    : largestNegativeMagnitude
      ? -largestNegativeMagnitude * 1.2
      : 0;
  const yAxisMax = useIndependentSignScale
    ? 1
    : largestPositiveValue
      ? largestPositiveValue * 1.2
      : largestNegativeMagnitude
        ? 0
        : 1;

  // Determine abbreviation suffix (K, L, M, Cr, B) based on max value
  const yAxisUnit = getAbbreviation(
    Math.max(largestPositiveValue, largestNegativeMagnitude, yAxisMax),
  );

  // --- CSS Variable Resolution ---
  // Extracts computed value from CSS custom property (removes "var()" wrapper)
  const resolveCssColor = (colorString) => {
    const varName = colorString.replace(/var\(|\)/g, "").trim();
    const resolved = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return resolved;
  };

  // Resolve color CSS variables to actual color values for MUI Charts
  const graphDataFirstColorResolved = resolveCssColor(graphDataFirstColor);
  const graphDataSecondColorResolved = resolveCssColor(graphDataSecondColor);
  const negativeColorResolved = resolveCssColor("var(--negative-act)");

  const zeroColorResolved = resolveCssColor(zeroValueColor);
  const nonZeroColorResolved = resolveCssColor(nonZeroValueColor);

  /**
   * Determines the active color for the second series line/area
   * Uses dynamic zero coloring when enabled, otherwise uses static color
   */
  const secondSeriesActiveColor = enableDynamicZeroColoring
    ? isHovered
      ? isHoveredSecondZero
        ? zeroColorResolved
        : nonZeroColorResolved
      : hasSecondDataNonZero
        ? nonZeroColorResolved
        : zeroColorResolved
    : graphDataSecondColorResolved;

  // --- Month Grouping Logic ---
  // Groups consecutive dates by month for bottom-axis month labels
  const monthGroups = [];
  let currentMonth = null;

  xAxisLabels.forEach((date, index) => {
    const month = dayjs(date).format("MMM YYYY");
    if (!currentMonth || currentMonth.name !== month) {
      currentMonth = { name: month, start: index, end: index };
      monthGroups.push(currentMonth);
    } else {
      currentMonth.end = index;
    }
  });

  /**
   * Gets the color for the second series at a specific index.
   * When dynamic zero coloring is enabled, returns zero/non-zero color based on value.
   */
  const getSecondSeriesColorAt = (index) => {
    if (!enableDynamicZeroColoring) return graphDataSecondColorResolved;
    const val =
      formattedGraphDataSecond[index] ?? GlobalConfiguration?.lengthCheckZero;
    return val === GlobalConfiguration?.lengthCheckZero
      ? zeroColorResolved
      : nonZeroColorResolved;
  };

  /**
   * Returns { ringColor, dotColor } for a mark at the given index.
   * Handles overlapping points where both series have the same value.
   */
  const getMarkColors = (isSecond, index) => {
    const hasSecond = !!graphDataSecond;
    const firstVal = formattedGraphDataFirst[index];
    const secondVal = formattedGraphDataSecond[index];
    const valuesAreSame =
      hasSecond && firstVal !== undefined && firstVal === secondVal;

    const secondColor = getSecondSeriesColorAt(index);

    // Both series overlap here -> ring = first series, center = second series
    if (valuesAreSame) {
      return { ringColor: graphDataFirstColorResolved, dotColor: secondColor };
    }

    // Normal case: ring and dot share the series color
    const color = isSecond ? secondColor : graphDataFirstColorResolved;
    return { ringColor: color, dotColor: color };
  };

  /**
   * HoverIndexWatcher - Tracks mouse position over chart to determine highlighted index.
   * Uses MUI's useXScale and useDrawingArea hooks to map mouse X to data index.
   */
  const HoverIndexWatcher = ({ onChange, maxIndex }) => {
    const scale = useXScale();
    const { left, width } = useDrawingArea();
    const gRef = useRef(null);

    useEffect(() => {
      const svg = gRef.current?.ownerSVGElement;
      if (!svg || !scale?.invert) return;

      const handleMove = (e) => {
        const rect = svg.getBoundingClientRect();
        const x = e.clientX - rect.left;
        // Pointer is outside the actual chart drawing area.
        if (x < left || x > left + width) {
          setIsSyntheticCrossingHovered(false);
          onChange(null);
          setHighlightedAxis([]);
          setTooltipAxis([]);
          return;
        }

        const chartX = scale.invert(x);

        /**
         * Synthetic zero-crossing points are stored as fractional
         * x values, for example:
         *
         *   10,000 -> -5,000
         *
         * crossingX = 0.6667
         *
         * They are visual-only and must not participate in the
         * normal MUI axis hover interaction.
         *
         * Use pixel distance instead of chart-value distance so
         * this remains consistent regardless of chart width/scale.
         */
        const syntheticCrossingX =
          splitBySign && signSplitData?.xData
            ? signSplitData.xData.find((xValue, dataIndex) => {
                if (!isSyntheticCrossingIndex(dataIndex)) return false;

                const syntheticPixelX = scale(xValue);
                const syntheticDistance = Math.abs(syntheticPixelX - x);
                const nearestActualDistance = Math.min(
                  ...signSplitData.xData
                    .map((actualX, actualIndex) =>
                      isSyntheticCrossingIndex(actualIndex)
                        ? Infinity
                        : Math.abs(scale(actualX) - x),
                    )
                    .filter(Number.isFinite),
                );

                // Suppress hover within 20px of a crossing, but keep actual
                // data points interactive by requiring the crossing to be
                // closer than every real point.
                return (
                  Number.isFinite(syntheticPixelX) &&
                  syntheticDistance <= 20 &&
                  syntheticDistance < nearestActualDistance
                );
              })
            : undefined;

        if (syntheticCrossingX !== undefined) {
          setIsSyntheticCrossingHovered(true);
          onChange(null);
          setHighlightedAxis([]); // force clear
          setTooltipAxis([]); // force clear
          return;
        }

        setIsSyntheticCrossingHovered(false);

        const idx = Math.round(chartX);

        onChange(
          Math.min(
            Math.max(idx, GlobalConfiguration?.lengthCheckZero),
            maxIndex,
          ),
        );
      };

      const handleLeave = () => {
        setIsSyntheticCrossingHovered(false);
        onChange(null);
        setHighlightedAxis([]);
        setTooltipAxis([]);
      };

      /**
       * Capture phase is intentional.
       *
       * MUI also listens to mouse movement on the SVG.
       * Running during capture allows us to identify the
       * synthetic crossing before the normal hover state is used.
       */
      svg.addEventListener("mousemove", handleMove, true);
      svg.addEventListener("mouseleave", handleLeave);

      return () => {
        svg.removeEventListener("mousemove", handleMove, true);
        svg.removeEventListener("mouseleave", handleLeave);
      };
    }, [scale, left, width, maxIndex, onChange]);

    return <g ref={gRef} />;
  };

  const handleAxisHighlight = (idx) => {
    setHighlightedItemIndex(idx);
  };

  /**
   * Allows the MUI axis highlight only for real data points.
   *
   * Synthetic zero-crossing points are used only to split the
   * green/red line visually. They must not behave like real
   * hoverable data points.
   */
  const handleHighlightedAxisChange = (axisItems) => {
    if (isSyntheticCrossingHovered || !splitBySign) {
      setHighlightedAxis(isSyntheticCrossingHovered ? [] : axisItems);
      return;
    }
    setHighlightedAxis(
      axisItems.filter((item) => !isSyntheticCrossingIndex(item.dataIndex)),
    );
  };

  const handleTooltipAxisChange = (axisItems) => {
    if (isSyntheticCrossingHovered || !splitBySign) {
      setTooltipAxis(isSyntheticCrossingHovered ? [] : axisItems);
      return;
    }
    setTooltipAxis(
      axisItems.filter((item) => !isSyntheticCrossingIndex(item.dataIndex)),
    );
  };

  /**
   * Concentric Mark Component:
   * - Inner solid dot: 7x7 (r = 3.5px)
   * - Outer stroke ring: 11x11 (r = 5.5px)
   * - Transparent MarkElement underneath to preserve all native MUI tooltip & hover events
   */
  const ConcentricMark = (props) => {
    const { dataIndex, id, seriesId, x, y } = props;

    // Determine series color
    const seriesKey = id ?? seriesId;

    /**
     * In split-by-sign mode, synthetic zero-crossing points are inserted
     * into the chart data, so MUI's dataIndex is not always the same as
     * the original graphDataFirst index.
     */
    const originalIndex = splitBySign
      ? signSplitData?.originalIndexMap[dataIndex]
      : dataIndex;

    /**
     * Synthetic zero-crossing points do not need a visible mark.
     */
    if (
      splitBySign &&
      (originalIndex === null || originalIndex === undefined)
    ) {
      return null;
    }

    const actualValue = graphDataFirst[originalIndex];

    if (splitBySign && seriesKey === "first" && actualValue < 0) {
      return null;
    }

    if (splitBySign && seriesKey === "negative" && actualValue >= 0) {
      return null;
    }
    const isSecond = seriesKey === "second";
    const { ringColor, dotColor } =
      seriesKey === "negative"
        ? {
            ringColor: negativeColorResolved,
            dotColor: negativeColorResolved,
          }
        : getMarkColors(isSecond, originalIndex);
    return (
      <g style={{ cursor: "default" }}>
        {/* 1. Official MUI MarkElement: Keeps all internal tooltip & interaction handlers active */}
        <MarkElement
          {...props}
          sx={{
            opacity: GlobalConfiguration?.lengthCheckZero,
            cursor: "default",
          }}
        />

        {/* 2. Outer stroke ring (11x11 -> radius 5.5px) */}
        <circle
          cx={x}
          cy={y}
          r={SettingsStatisticsConfiguration?.outerStrokeRingRadius}
          fill="none"
          stroke={ringColor}
          strokeWidth={SettingsStatisticsConfiguration?.outerStrokeWidth}
          style={{ pointerEvents: "none" }}
        />

        {/* 3. Inner solid circle (7x7 -> radius 3.5px) */}
        <circle
          cx={x}
          cy={y}
          r={SettingsStatisticsConfiguration?.innerSolidCircleRadius}
          fill={dotColor}
          stroke="none"
          style={{ pointerEvents: "none" }}
        />
      </g>
    );
  };

  /**
   * Custom LineHighlight Component:
   * Renders the hover highlight only for the series that owns
   * the actual sign of the hovered value.
   *
   * The split-by-sign graph intentionally contains transition points
   * in both series so that the green-to-red line connects correctly.
   * However, only one series should receive the hover highlight:
   *
   *   positive / zero value -> first (green) series
   *   negative value        -> negative (red) series
   */
  const ConcentricHighlight = (props) => {
    const { x, y, id, seriesId } = props;

    const seriesKey = id ?? seriesId;

    // Use the original date index from the hover watcher. In sign-split
    // mode MUI's data index refers to the expanded series, which includes
    // synthetic zero-crossing points and does not map directly to the data.
    const originalIndex = highlightedItemIndex;

    const actualValue = graphDataFirst[originalIndex];

    // No valid hovered index/value -> don't render a highlight.
    if (
      originalIndex === null ||
      originalIndex === undefined ||
      actualValue === undefined ||
      actualValue === null
    ) {
      return null;
    }
    /**
     * Only the correct sign series should render the hover highlight.
     *
     * At transition points, both series contain the same value internally
     * to keep the line continuous. Without this check, the red highlight
     * can be rendered over a positive value.
     */
    if (splitBySign) {
      // Positive / zero value belongs to the first (green) series.
      if (actualValue >= 0 && seriesKey === "negative") {
        return null;
      }

      // Negative value belongs to the negative (red) series.
      if (actualValue < 0 && seriesKey === "first") {
        return null;
      }
    }

    const isSecond = seriesKey === "second";

    let markColor = graphDataFirstColorResolved;

    const { dotColor } =
      seriesKey === "negative"
        ? {
            dotColor: negativeColorResolved,
          }
        : getMarkColors(isSecond, originalIndex);

    if (seriesKey === "negative") {
      markColor = negativeColorResolved;
    } else if (isSecond) {
      if (enableDynamicZeroColoring) {
        const val =
          formattedGraphDataSecond[originalIndex] ??
          GlobalConfiguration?.lengthCheckZero;

        const isZero = val === GlobalConfiguration?.lengthCheckZero;

        markColor = isZero ? zeroColorResolved : nonZeroColorResolved;
      } else {
        markColor = graphDataSecondColorResolved;
      }
    }

    return (
      <g style={{ pointerEvents: "none" }}>
        <circle
          cx={x}
          cy={y}
          r={SettingsStatisticsConfiguration?.innerSolidCircleRadius}
          fill={dotColor}
          fillOpacity={1}
        />

        <circle
          cx={x}
          cy={y}
          r={
            SettingsStatisticsConfiguration?.innerSolidCircleRadius +
            SettingsStatisticsConfiguration?.outerStrokeWidth
          }
          fill={markColor}
          fillOpacity={1}
        />
      </g>
    );
  };
  /**
   * MonthLabels - Custom SVG component rendering month range labels below x-axis.
   * Draws vertical ticks, horizontal lines, and abbreviated month names centered
   * over each month's date range. Uses MUI Charts' internal scale and drawing area.
   */
  const MonthLabels = ({ monthGroups }) => {
    const scale = useXScale();
    const { top, height } = useDrawingArea();

    if (
      !scale ||
      !monthGroups ||
      monthGroups.length === GlobalConfiguration?.lengthCheckZero
    )
      return null;

    // Y position for labels: below chart area + configured offset
    const yLine =
      top + height + SettingsStatisticsConfiguration?.graphRangeLabelOffsetY;
    const tickHeight =
      SettingsStatisticsConfiguration?.graphRangeLabelEdgeHeight;

    return (
      <g className="month-labels-group">
        {monthGroups.map((group, idx) => {
          const isSingleDate = group.start === group.end;

          let xStart, xEnd;
          if (isSingleDate) {
            // Single date: center label with half-width on each side
            const xCenter = scale(group.start);
            xStart =
              xCenter -
              SettingsStatisticsConfiguration?.graphRangeLabelSingleDateHalfWidth;
            xEnd =
              xCenter +
              SettingsStatisticsConfiguration?.graphRangeLabelSingleDateHalfWidth;
          } else {
            // Multi-date range: span from first to last date
            xStart = scale(group.start);
            xEnd = scale(group.end);
          }

          // Skip if scale returned invalid values
          if (
            xStart === undefined ||
            xEnd === undefined ||
            isNaN(xStart) ||
            isNaN(xEnd)
          ) {
            return null;
          }

          const xMid = (xStart + xEnd) / GlobalConfiguration?.lengthCheckTwo;
          // Abbreviate month name to first 3 chars (e.g., "Jan 2025" -> "Jan")
          const monthName = group.name
            ? group.name.slice(
                GlobalConfiguration?.lengthCheckZero,
                GlobalConfiguration?.lengthCheckThree,
              )
            : "";
          const gap = SettingsStatisticsConfiguration?.graphMonthLabelGapX;
          return (
            <g key={idx}>
              {/* Left vertical tick */}
              <line
                x1={xStart}
                x2={xStart}
                y1={yLine - tickHeight / GlobalConfiguration?.lengthCheckTwo}
                y2={yLine + tickHeight / GlobalConfiguration?.lengthCheckTwo}
                stroke="var(--primary-nav-act)"
                strokeWidth={
                  SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                }
              />

              {/* Left horizontal line (only for multi-date ranges) */}
              {!isSingleDate && xStart < xMid - gap && (
                <line
                  x1={xStart}
                  x2={xMid - gap}
                  y1={yLine}
                  y2={yLine}
                  stroke="var(--primary-nav-act)"
                  strokeWidth={
                    SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                  }
                />
              )}

              {/* Month label text */}
              <text
                x={xMid}
                y={yLine}
                fill="var(--text-secondary)"
                fontSize="var(--font-xs)"
                fontWeight="var(--font-normal)"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {monthName}
              </text>

              {/* Right horizontal line (only for multi-date ranges) */}
              {!isSingleDate && xMid + gap < xEnd && (
                <line
                  x1={xMid + gap}
                  x2={xEnd}
                  y1={yLine}
                  y2={yLine}
                  stroke="var(--primary-nav-act)"
                  strokeWidth={
                    SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                  }
                />
              )}

              {/* Right vertical tick */}
              <line
                x1={xEnd}
                x2={xEnd}
                y1={yLine - tickHeight / GlobalConfiguration?.lengthCheckTwo}
                y2={yLine + tickHeight / GlobalConfiguration?.lengthCheckTwo}
                stroke="var(--primary-nav-act)"
                strokeWidth={
                  SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                }
              />
            </g>
          );
        })}
      </g>
    );
  };

  /**
   * Converts a numeric value to abbreviated string based on given unit.
   * E.g., convertValue(150000, "L") -> "1.5L"
   */
  function convertValue(value, abbreviation) {
    if (value === GlobalConfiguration?.lengthCheckZero)
      return GlobalConfiguration?.dashboardPrimaryCount.slice(-1);

    const scales = {
      "K": 1e3,
      "L": 1e5,
      "Cr": 1e7,
      "M": 1e6,
      "B": 1e9,
      "": 1,
    };

    const divisor = scales[abbreviation] ?? 1;
    const result = value / divisor;

    // Trim trailing zeros, keep 2 decimal places max
    return `${parseFloat(result.toFixed(2))}${abbreviation}`;
  }

  /**
   * Determines the appropriate abbreviation suffix for a given max value.
   * Thresholds follow numbering system (K, Lakh, Crore, M, B).
   */
  function getAbbreviation(maxValue) {
    const thresholds = [
      { limit: 1e3, suffix: "K" }, // >= 1K
      { limit: 1e5, suffix: "L" }, // >= 100K (1 Lakh)
      { limit: 1e6, suffix: "M" }, // >= 10 Lakhs (1 Million)
      { limit: 1e7, suffix: "Cr" }, // >= 100 Lakhs (1 Crore)
      { limit: 1e9, suffix: "B" }, // >= 1000 Crores (1 Billion)
      { limit: 0, suffix: "" }, // default, no abbreviation
    ];
    return thresholds.find((t) => maxValue >= t.limit).suffix;
  }

  /**
   * Builds separate positive and negative series while splitting
   * sign-changing segments exactly at the mathematical zero crossing.
   *
   * Important:
   * - Positive values belong only to the positive series.
   * - Negative values belong only to the negative series.
   * - Actual zero values belong to the positive series because zero is
   *   nonnegative; the negative series must stop at zero.
   * - A synthetic zero point is inserted when a segment crosses zero
   *   between two non-zero dates.
   * - The zero-crossing position is calculated using the ORIGINAL
   *   values, not normalized chart values.
   */
  const buildSignSplitData = () => {
    const positiveData = [];
    const negativeData = [];
    const xData = [];
    const originalIndexMap = [];

    const addPoint = (
      x,
      value,
      originalIndex,
      includeIncomingNegativeZero = false,
    ) => {
      xData.push(x);
      originalIndexMap.push(originalIndex);

      // Zero belongs to the positive (green) series. The negative series may
      // use it only as the endpoint of an incoming negative segment.
      if (value === 0) {
        positiveData.push(0);
        // Keep the incoming negative segment connected to its zero endpoint.
        // The negative series still ends here; later zero points are green only.
        negativeData.push(includeIncomingNegativeZero ? 0 : null);
        return;
      }

      // Positive value -> green series only.
      if (value > 0) {
        positiveData.push(value);
        negativeData.push(null);
        return;
      }

      // Negative value -> red series only.
      positiveData.push(null);
      negativeData.push(value);
    };

    // Add the first actual data point.
    if (formattedGraphDataFirst.length > 0) {
      addPoint(0, chartDataFirst[0], 0);
    }

    for (let index = 1; index < formattedGraphDataFirst.length; index++) {
      const previousActualValue = Number(formattedGraphDataFirst[index - 1]);

      const currentActualValue = Number(formattedGraphDataFirst[index]);

      const previousChartValue = chartDataFirst[index - 1];
      const currentChartValue = chartDataFirst[index];

      /**
       * If both values are non-zero and their signs are different,
       * insert a synthetic zero point between them.
       *
       * Example:
       *
       *   2000 ----> -1000
       *
       * becomes:
       *
       *   2000 ----> 0 ----> -1000
       */
      const crossesZero =
        previousActualValue !== 0 &&
        currentActualValue !== 0 &&
        ((previousActualValue > 0 && currentActualValue < 0) ||
          (previousActualValue < 0 && currentActualValue > 0));

      if (crossesZero) {
        /**
         * Calculate the zero-crossing position using the ORIGINAL
         * values.
         *
         * Do not use chartDataFirst here because positive and negative
         * values may have independent normalization scales.
         */
        const crossingRatio =
          Math.abs(previousActualValue) /
          (Math.abs(previousActualValue) + Math.abs(currentActualValue));

        const crossingX = index - 1 + crossingRatio;

        // Synthetic zero point belongs to BOTH series.
        xData.push(crossingX);
        originalIndexMap.push(null);
        positiveData.push(0);
        negativeData.push(0);
      }

      /**
       * Add the actual current point.
       *
       * If currentActualValue === 0, the positive series owns that date.
       * Also give the negative series a zero endpoint only when the
       * preceding value was negative, preserving the incoming red segment.
       */
      addPoint(
        index,
        currentChartValue,
        index,
        currentActualValue === 0 && previousActualValue < 0,
      );
    }

    return {
      xData,
      positiveData,
      negativeData,
      originalIndexMap,
    };
  };

  const signSplitData = splitBySign ? buildSignSplitData() : null;
  const isSyntheticCrossingIndex = (index) => {
    if (!splitBySign || !signSplitData) {
      return false;
    }

    return (
      index !== null &&
      index !== undefined &&
      (signSplitData.originalIndexMap[index] === null ||
        signSplitData.originalIndexMap[index] === undefined)
    );
  };

  return (
    <div className="relative">
      <LineChart
        grid={{ vertical: true }}
        tooltip={{
          trigger: isSyntheticCrossingHovered ? "none" : "axis",
        }}
        highlightedAxis={isSyntheticCrossingHovered ? [] : highlightedAxis}
        tooltipAxis={isSyntheticCrossingHovered ? [] : tooltipAxis}
        onHighlightedAxisChange={handleHighlightedAxisChange}
        onTooltipAxisChange={handleTooltipAxisChange}
        slots={{
          mark: ConcentricMark,
          lineHighlight: ConcentricHighlight,
        }}
        margin={{
          right: SettingsStatisticsConfiguration.graphPaddingRight, // right padding
          left: SettingsStatisticsConfiguration.graphPaddingLeft, // left padding
        }}
        series={
          splitBySign
            ? [
                {
                  id: "first",
                  data: signSplitData.positiveData,
                  area: true,
                  curve: "linear",
                  connectNulls: false,
                  showMark: ({ index }) =>
                    signSplitData.originalIndexMap[index] !== null &&
                    signSplitData.originalIndexMap[index] !== undefined &&
                    graphDataFirst[signSplitData.originalIndexMap[index]] >= 0,

                  baseline: 0,
                  color: graphDataFirstColorResolved,
                  valueFormatter: (value, { dataIndex }) => {
                    const originalIndex =
                      signSplitData.originalIndexMap[dataIndex];

                    // Synthetic zero-crossing → never show tooltip
                    if (originalIndex === null || originalIndex === undefined) {
                      return null;
                    }

                    const actualValue = graphDataFirst[originalIndex];

                    // This series only owns non-negative values
                    if (actualValue < 0) {
                      return null;
                    }

                    return amountFormatRequiredForLegend
                      ? `INR ${
                          formatTotalBalance(fromChartValue(value), "amount")
                            .amount
                        }${amountFormatForLegend ? ` ${amountFormatForLegend}` : ""}`
                      : value;
                  },
                },
                {
                  id: "negative",
                  data: signSplitData.negativeData,
                  area: true,
                  curve: "linear",
                  connectNulls: false,
                  showMark: ({ index }) => {
                    const originalIndex = signSplitData.originalIndexMap[index];

                    return (
                      originalIndex !== null &&
                      originalIndex !== undefined &&
                      graphDataFirst[originalIndex] < 0
                    );
                  },
                  baseline: 0,
                  color: negativeColorResolved,
                  valueFormatter: (value, { dataIndex }) => {
                    const originalIndex =
                      signSplitData.originalIndexMap[dataIndex];

                    // Synthetic zero-crossing point.
                    if (originalIndex === null || originalIndex === undefined) {
                      return null;
                    }

                    const actualValue = graphDataFirst[originalIndex];

                    if (actualValue >= 0) {
                      return null;
                    }

                    return amountFormatRequiredForLegend
                      ? `INR ${
                          formatTotalBalance(fromChartValue(value), "amount")
                            .amount
                        }${
                          amountFormatForLegend
                            ? ` ${amountFormatForLegend}`
                            : ""
                        }`
                      : value;
                  },
                },
              ]
            : graphDataSecond
              ? [
                  // First series (primary)
                  {
                    id: "first",
                    data: chartDataFirst,
                    area: true,
                    curve: "linear",
                    showMark: true,
                    baseline: "min",
                    color: graphDataFirstColorResolved,
                    valueFormatter: (value) =>
                      amountFormatRequiredForLegend
                        ? `INR ${formatTotalBalance(fromChartValue(value), "amount").amount}${
                            amountFormatForLegend
                              ? " " + amountFormatForLegend
                              : ""
                          }`
                        : value,
                  },
                  // Second series (secondary)
                  {
                    id: "second",
                    data: chartDataSecond,
                    area: true,
                    curve: "linear",
                    showMark: true,
                    baseline: "min",
                    color: secondSeriesActiveColor,
                    valueFormatter: (value) =>
                      amountFormatRequiredForLegend
                        ? `INR ${formatTotalBalance(fromChartValue(value), "amount").amount}${
                            amountFormatForLegend
                              ? " " + amountFormatForLegend
                              : ""
                          }`
                        : value,
                  },
                ]
              : [
                  // Single series mode
                  {
                    id: "first",
                    data: chartDataFirst,
                    area: true,
                    curve: "linear",
                    showMark: true,
                    baseline: "min",
                    color: graphDataFirstColorResolved,
                    valueFormatter: (value) =>
                      amountFormatRequiredForLegend
                        ? `INR ${formatTotalBalance(fromChartValue(value), "amount").amount}${
                            amountFormatForLegend
                              ? " " + amountFormatForLegend
                              : ""
                          }`
                        : value,
                  },
                ]
        }
        className={`settings-statistics-linechart${isSyntheticCrossingHovered ? " synthetic-crossing-hover" : ""}`}
        xAxis={[
          {
            scaleType: "linear",
            // Use custom indices if provided, otherwise 0..n
            tickInterval:
              defaultDataItemsForXaxis || xAxisArray.map((_, i) => i),
            valueFormatter: (value, context) => {
              const item = xAxisArray[value];
              if (!item) return "";
              const d = dayjs(item);
              // Tooltip header
              if (context?.location === "tooltip") {
                return d.isValid() ? d.format("DD MMM") : item;
              }

              // If valid date, show day number (DD); otherwise show first 3 chars
              return d.isValid()
                ? d.format("DD")
                : item.slice(
                    GlobalConfiguration?.lengthCheckZero,
                    GlobalConfiguration?.lengthCheckThree,
                  );
            },
            data: splitBySign ? signSplitData.xData : defaultDataItemsForXaxis,
            tickMinStep: GlobalConfiguration?.lengthCheckOne,
            // Add padding on left/right of chart for first/last points
            min: -(
              (xAxisArray.length - GlobalConfiguration?.lengthCheckOne) *
              SettingsStatisticsConfiguration?.graphXAxisPaddingPercent
            ),
            max:
              (xAxisArray.length - GlobalConfiguration?.lengthCheckOne) *
              (GlobalConfiguration?.lengthCheckOne +
                SettingsStatisticsConfiguration?.graphXAxisPaddingPercent),
            showGrid: true,
            tickLabelStyle: { fontSize: "var(--font-xxs)" },
          },
        ]}
        yAxis={[
          {
            valueFormatter: (value) => {
              const actualValue = fromChartValue(value);
              // If user selected a specific unit, format with that unit's abbreviation
              if (
                amountFormatForLegend &&
                actualValue !== GlobalConfiguration?.lengthCheckZero
              ) {
                return `${formatTotalBalance(actualValue, "amount")
                  .amount.replace(",", "")
                  .concat("" + abbrMap?.[amountFormatForLegend] || "")}`;
              }
              // Otherwise use auto-detected abbreviation based on data range
              return convertValue(actualValue, yAxisUnit);
            },
            scaleType: "linear",
            tickNumber: SettingsStatisticsConfiguration?.graphYAxisTickCount,
            min: yAxisMin,
            max: yAxisMax,
            tickLabelStyle: { fontSize: "var(--font-xxs)" },
          },
        ]}
        style={{
          height: "var(--statistic-graph-height)",
          width: "var(--width-full)",
        }}
        sx={{
          // ---------------First series styles----------------//
          "& .MuiLineElement-series-first": {
            stroke: graphDataFirstColor,
            strokeDasharray: "4 4", // dotted line
          },
          "& .MuiAreaElement-series-first": {
            fill: graphDataFirstColor,
            fillOpacity:
              SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
          "& .MuiLineElement-series-negative": {
            stroke: negativeColorResolved,
            strokeDasharray: "4 4",
          },
          "& .MuiAreaElement-series-negative": {
            fill: negativeColorResolved,
            fillOpacity:
              SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },

          // -----------Second series styles----------------//
          "& .MuiLineElement-series-second": {
            stroke: enableDynamicZeroColoring
              ? hasSecondDataNonZero
                ? "url(#secondSeriesLineGradient)"
                : zeroColorResolved
              : graphDataSecondColor,
            strokeDasharray: "4 4", // dotted line
          },
          "& .MuiAreaElement-series-second": {
            fill: enableDynamicZeroColoring
              ? hasSecondDataNonZero
                ? "url(#secondSeriesAreaGradient)"
                : "transparent"
              : graphDataSecondColor,
            fillOpacity:
              SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
          // ------------Common styling---------------------//

          "& .MuiChartsAxisHighlight-root": {
            strokeDasharray:
              SettingsStatisticsConfiguration.zeroStrokeWidthForLineGraph,
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
            stroke: "var(--text-primary)",
          },
          // Axis lines
          "& .MuiChartsAxis-root line": {
            stroke: "var(--text-primary)",
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
          },
          // Axis tick marks
          "& .MuiChartsAxis-tick text": {
            fill: "var(--text-primary)",
            fontSize: "var(--font-sm)",
          },
          "& .MuiChartsAxis-tickLabel": {
            fill: "var(--text-primary) !important",
          },

          // Hide horizontal grid lines (cleaner look)
          "& .MuiChartsGrid-horizontalLine": {
            display: "none",
          },

          // Hide Y-axis line and ticks (keep numeric labels only)
          "& .MuiChartsAxis-left .MuiChartsAxis-line": {
            display: "none",
          },
          "& .MuiChartsAxis-left .MuiChartsAxis-tick": {
            display: "none",
          },

          // Style vertical grid lines
          "& .MuiChartsGrid-verticalLine": {
            stroke: "var(--text-secondary)",
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
            opacity: SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
          ...(isSyntheticCrossingHovered && {
            // Hide the empty tooltip completely
            "& .MuiChartsTooltip-root, & .MuiChartsTooltip-table, & .MuiPopper-root":
              {
                display: "none !important",
                visibility: "hidden !important",
                opacity: 0,
                pointerEvents: "none",
              },
            // Hide the vertical highlight line
            "& .MuiChartsAxisHighlight-root": {
              display: "none !important",
            },
          }),
        }}
      >
        <HoverIndexWatcher
          onChange={handleAxisHighlight}
          maxIndex={xAxisArray.length - GlobalConfiguration?.lengthCheckOne}
        />
        {enableDynamicZeroColoring && (
          <defs>
            <linearGradient
              id="secondSeriesLineGradient"
              x1="0"
              y1="1"
              x2="0"
              y2="0"
            >
              <stop
                offset="0%"
                stopColor={zeroColorResolved}
              />
              <stop
                offset="1%"
                stopColor={zeroColorResolved}
              />
              <stop
                offset="2%"
                stopColor={nonZeroColorResolved}
              />
              <stop
                offset="100%"
                stopColor={nonZeroColorResolved}
              />
            </linearGradient>
            <linearGradient
              id="secondSeriesAreaGradient"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor={nonZeroColorResolved}
                stopOpacity={
                  SettingsStatisticsConfiguration?.fillOpacityForLineGraph
                }
              />
              <stop
                offset="100%"
                stopColor={nonZeroColorResolved}
                stopOpacity={
                  SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                }
              />
            </linearGradient>
          </defs>
        )}

        {/* Render month labels only when x-axis contains full dates */}
        {isDateArray(xAxisLabels) &&
          monthGroups?.length > GlobalConfiguration?.lengthCheckZero && (
            <MonthLabels monthGroups={monthGroups} />
          )}
      </LineChart>
    </div>
  );
};

export default CommonLineGraph;
